import type { StepInitStatus } from 'components/layout';
import type { AdSettingsByIndex } from 'hooks';
import type { DecodedPackCreativesGroup } from 'hooks/useLocationParams';
import type { AdSettingEntry } from '../api/getAdSettings';

export const DEEP_LINK_STEP_LABELS = [
  'Creatives',
  'Ad Accounts & Platform',
  'Campaigns',
  'Ad Sets',
  'Ad',
] as const;

export interface DeepLinkFailedStepMessage {
  label: string;
  text: string;
}

export const collectUrlCreativeIds = (
  creativesFormat: 'pack' | 'flat' | 'empty',
  creativesByPack: readonly DecodedPackCreativesGroup[],
  flatCreatives: readonly string[]
): string[] => {
  const rawIds =
    creativesFormat === 'flat'
      ? flatCreatives
      : creativesFormat === 'pack'
        ? creativesByPack.flatMap((group) => group.creatives)
        : [];

  return [...new Set(rawIds.map((id) => String(id)))];
};

export const collectMissingAdSettingIds = (
  slots: AdSettingsByIndex,
  getFlow: (index: number, flowName: string) => AdSettingEntry | undefined
): string[] => {
  const missing: string[] = [];

  slots.forEach((slot, index) => {
    Object.entries(slot ?? {}).forEach(([flowName, selection]) => {
      const flow = getFlow(index, flowName);
      if (!flow) {
        missing.push(flowName);
        return;
      }

      const pageIds = new Set(
        (flow.pages ?? []).map((page) => String(page.id))
      );
      const textIds = new Set(
        (flow.texts ?? []).map((text) => String(text.id))
      );

      if (selection.page_id && !pageIds.has(String(selection.page_id))) {
        missing.push(String(selection.page_id));
      }
      if (selection.text_id && !textIds.has(String(selection.text_id))) {
        missing.push(String(selection.text_id));
      }
    });
  });

  return missing;
};

export const resolveFetchedStepStatus = ({
  enabled,
  previousPass = true,
  emptySuccess = false,
  isPending,
  isError,
  hasMissing,
}: {
  enabled: boolean;
  previousPass?: boolean;
  emptySuccess?: boolean;
  isPending: boolean;
  isError: boolean;
  hasMissing: boolean;
}): StepInitStatus => {
  if (!enabled || !previousPass) return 'idle';
  if (emptySuccess) return 'success';
  if (isPending) return 'loading';
  if (isError || hasMissing) return 'failed';
  return 'success';
};

export const isDeepLinkStepPass = (
  enabled: boolean,
  status: StepInitStatus
): boolean => !enabled || status === 'success';

const toFailedStepMessage = (
  label: string,
  missingIds: readonly string[]
): DeepLinkFailedStepMessage =>
  missingIds.length > 0
    ? { label, text: `Not found: ${missingIds.join(', ')}` }
    : { label, text: 'Failed to load data' };

export const buildDeepLinkFailedMessages = ({
  statuses,
  missingByStep,
  mismatchMessage,
}: {
  statuses: readonly StepInitStatus[];
  missingByStep: readonly (readonly string[])[];
  mismatchMessage?: string;
}): DeepLinkFailedStepMessage[] => {
  if (statuses[0] === 'failed') {
    if (mismatchMessage) {
      return [{ label: DEEP_LINK_STEP_LABELS[0], text: mismatchMessage }];
    }
    return [
      toFailedStepMessage(DEEP_LINK_STEP_LABELS[0], missingByStep[0] ?? []),
    ];
  }

  return [1, 2, 3, 4].flatMap((stepIndex) => {
    if (statuses[stepIndex] !== 'failed') return [];

    const label = DEEP_LINK_STEP_LABELS[stepIndex] ?? `Step ${stepIndex + 1}`;
    return [toFailedStepMessage(label, missingByStep[stepIndex] ?? [])];
  });
};
